import React, { useEffect, useRef } from 'react';
import { LayoutDashboard, LineChart, Plug, SlidersHorizontal, X } from 'lucide-react';

const FEATURES = [
  {
    icon: LayoutDashboard,
    title: 'Single operational view',
    body: 'Bring statuses, ownership, and blockers into one executive-ready surface.',
  },
  {
    icon: LineChart,
    title: 'Live KPIs and trends',
    body: 'Track what moved this week with clear baselines, not spreadsheet archaeology.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Role-based layouts',
    body: 'Tune dashboards for leadership, delivery leads, or specialists without duplicating work.',
  },
  {
    icon: Plug,
    title: 'Fits your toolchain',
    body: 'Connects to the systems you already run—CRM, finance, delivery, and reporting.',
  },
];

/**
 * Full-screen preview for the agency Second Brain / command centre thumbnail.
 * Dark shell (#262a32) to match design mockup; copy uses British English where relevant.
 */
export function OperationsDashboardExampleModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-hidden={!open}>
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div className="relative z-[101] flex max-h-[100dvh] min-h-full items-center justify-center overflow-y-auto p-3 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-example-title"
          className="portal-modal-panel relative my-auto rounded-2xl border border-white/[0.12] bg-[#262a32] px-4 py-6 shadow-2xl sm:px-8 sm:py-8"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="absolute right-3 top-3 rounded-full border border-white/20 bg-white/5 p-2 text-white/85 shadow-sm backdrop-blur-sm transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600] sm:right-4 sm:top-4"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>

          <header className="pr-10 text-center sm:pr-12 sm:text-left">
            <h2 id="dashboard-example-title" className="sr-only">
              Second Brain — Agency Command Centre
            </h2>
            <img
              src="/assets/images/Dashboard_Modal_Logo.webp"
              alt=""
              width={560}
              height={112}
              className="mx-auto h-20 w-auto max-w-[min(100%,560px)] object-contain object-center sm:mx-0 sm:h-24"
              loading="eager"
              decoding="async"
            />
            <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-[#b8bcc4] sm:mx-0 sm:mt-5 sm:text-lg">
              A command-centre view of delivery, commercial health, and risk—built for leaders who need
              signal without another tab marathon.
            </p>
          </header>

          <div className="mt-8 overflow-hidden rounded-xl bg-[#262a32]">
            <img
              src="/assets/images/Dashboard_modal_example.webp"
              alt="Second Brain agency command centre: analytics and reporting layouts"
              width={1200}
              height={800}
              className="h-auto w-full object-contain"
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="mt-8 rounded-xl bg-[#171b21] px-4 py-5 sm:px-6 sm:py-6">
            <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex gap-3 text-left">
                  <Icon
                    className="mt-0.5 h-8 w-8 shrink-0 text-[#f6793b]"
                    strokeWidth={1.5}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-white sm:text-lg">{title}</p>
                    <p className="mt-1 text-base leading-relaxed text-[#aeb6c4] sm:text-lg">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OperationsDashboardExampleModal;
